import { Component, DestroyRef, ElementRef, inject, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LibraryGroup } from '../../models/chat-library.models';
import { GroupHeaderComponent } from '../group-header/group-header.component';
import { GroupContentComponent } from '../group-content/group-content.component';
import { CreativeCardGridComponent } from '../creative-card-grid/creative-card-grid.component';
import { ChatLibraryService } from '../../services/chat-library.service';

const SCROLL_CONTAINER_SELECTOR = '.chat-library-container';
const STICKY_HEADER_OFFSET = 100;
const SCROLL_DELAY_MS = 300;
const FOCUS_DURATION_MS = 5000;

@Component({
  selector: 'app-library-group',
  standalone: true,
  imports: [GroupHeaderComponent, GroupContentComponent, CreativeCardGridComponent],
  templateUrl: './library-group.component.html',
  styleUrl: './library-group.component.scss',
})
export class LibraryGroupComponent implements OnInit {
  @Input({ required: true }) group!: LibraryGroup;

  protected readonly isFocused = signal(false);

  private readonly chatLibraryService = inject(ChatLibraryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly el = inject(ElementRef<HTMLElement>);

  ngOnInit(): void {
    this.chatLibraryService.scrollToGroup$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(groupId => {
        if (groupId !== this.group.id) return;
        this.isFocused.set(true);
        setTimeout(() => this.scrollIntoViewWithOffset(STICKY_HEADER_OFFSET), SCROLL_DELAY_MS);
        setTimeout(() => this.isFocused.set(false), FOCUS_DURATION_MS);
      });
  }

  onFindInChat(): void {
    this.chatLibraryService.focusOnGroup(this.group);
  }

  private scrollIntoViewWithOffset(offset: number): void {
    const target = this.el.nativeElement;
    const scrollContainer = target.closest(SCROLL_CONTAINER_SELECTOR) as HTMLElement | null;
    if (!scrollContainer) {
      target.scrollIntoView({behavior: 'smooth', block: 'start'});
      return;
    }
    const scrollTop = scrollContainer.scrollTop
      + target.getBoundingClientRect().top
      - scrollContainer.getBoundingClientRect().top
      - offset;
    scrollContainer.scrollTo({top: scrollTop, behavior: 'smooth'});
  }
}
